import {
  KHASARA_BLOCK_ABSORBED_UIDS,
  KHASARA_BLOCK_UIDS,
  NEVER_DRAWN_UIDS,
  PINNED_END_FIELD_UIDS,
  START_AFTER_KHASARA_BLOCK_UIDS,
  START_BEFORE_KHASARA_BLOCK_UIDS,
} from "./applicantFormLayout";

/**
 * Ordering rules of the Field Sequence feature, kept free of React and of the
 * server so the Admin screen and the applicant form apply exactly the same
 * rules.
 *
 * The order is one list of items for the whole applicant form: a field, or an
 * empty space the Admin put on one form step. A step shows its own fields and
 * empty spaces, in the order they have in the list.
 */

/** One field of the applicant form as the Admin screen receives it. */
export type TSequenceField = {
  id: number;
  uid: string;
  label: string;
  /** Names of the forms (steps) the field sits on. */
  forms: string[];
  /** Ids of those forms. */
  formIds: number[];
};

/** One saved place in the order, as the server stores it. */
export type TSavedItem = { fieldId: number } | { spacerFormId: number };

/** What GET /field-sequence/admin returns. */
export type TFieldSequenceAdminView = {
  /** The applicant form's switch. */
  enabled: boolean;
  /** The review screens' switch; missing from a server without it. */
  reviewEnabled?: boolean;
  fields: TSequenceField[];
  /** The saved order, first position first; empty when nothing was saved. */
  savedItems: TSavedItem[];
  /** Names of the services that have an applicant form. */
  services: string[];
};

/** One place in the order while it is edited or drawn. */
export type TOrderItem =
  | { key: string; kind: "field"; fieldId: number }
  | { key: string; kind: "spacer"; formId: number };

type TLayoutField = { id: number; uid: string };

const UNPLACED = Number.POSITIVE_INFINITY;
const neverDrawn = new Set(NEVER_DRAWN_UIDS);

export const fieldKey = (id: number) => `field-${id}`;

let spacerSeq = 0;

/** A new empty space on one form step, with a key no other item has. */
export function newSpacer(formId: number): TOrderItem {
  spacerSeq += 1;
  return { key: `spacer-new-${spacerSeq}`, kind: "spacer", formId };
}

/** Sorts by rank and keeps the incoming order between equal ranks. */
function stableSortByRank<T>(items: readonly T[], rankOf: (item: T) => number): T[] {
  return items
    .map((item, index) => ({ item, index, rank: rankOf(item) }))
    .sort((a, b) => (a.rank === b.rank ? a.index - b.index : a.rank < b.rank ? -1 : 1))
    .map((entry) => entry.item);
}

/**
 * Fields in the form's default order: pinned top fields, Mauja / Khasara No. /
 * Plot No. / Manual Sub-Khasara together (when the full trio is there), the
 * remaining fields in field-id order, and the pinned bottom fields. Fields
 * the form never draws are left out.
 */
export function defaultFieldOrder<F extends TLayoutField>(fields: readonly F[]): F[] {
  const drawn = fields.filter((field) => !neverDrawn.has(field.uid));
  const byUid = new Map(drawn.map((field) => [field.uid, field]));
  const pick = (uids: readonly string[]) =>
    uids.map((uid) => byUid.get(uid)).filter((f): f is F => !!f);

  const hasFullBlock = KHASARA_BLOCK_UIDS.every((uid) => byUid.has(uid));
  const block = hasFullBlock ? pick([...KHASARA_BLOCK_UIDS, ...KHASARA_BLOCK_ABSORBED_UIDS]) : [];

  const pinned = new Set([
    ...START_BEFORE_KHASARA_BLOCK_UIDS,
    ...block.map((field) => field.uid),
    ...START_AFTER_KHASARA_BLOCK_UIDS,
    ...PINNED_END_FIELD_UIDS,
  ]);
  const middle = drawn.filter((field) => !pinned.has(field.uid)).sort((a, b) => a.id - b.id);

  return [
    ...pick(START_BEFORE_KHASARA_BLOCK_UIDS),
    ...block,
    ...pick(START_AFTER_KHASARA_BLOCK_UIDS),
    ...middle,
    ...pick(PINNED_END_FIELD_UIDS),
  ];
}

/** Saved items as order items. Saved empty spaces get keys by their place. */
export function savedToOrder(savedItems: readonly TSavedItem[]): TOrderItem[] {
  return savedItems.map((item, index): TOrderItem =>
    "fieldId" in item
      ? { key: fieldKey(item.fieldId), kind: "field", fieldId: item.fieldId }
      : { key: `spacer-${index}`, kind: "spacer", formId: item.spacerFormId }
  );
}

/**
 * The whole order for the Admin screen: the saved items whose fields still
 * exist, then every field the saved order does not mention, in default order.
 * With nothing saved this is the default order.
 */
export function buildOrder(
  fields: readonly TSequenceField[],
  savedItems: readonly TSavedItem[]
): TOrderItem[] {
  const drawnIds = new Set(
    fields.filter((field) => !neverDrawn.has(field.uid)).map((field) => field.id)
  );
  const saved = savedToOrder(savedItems).filter(
    (item) => item.kind === "spacer" || drawnIds.has(item.fieldId)
  );
  const placed = new Set(saved.map((item) => item.key));
  const rest = defaultFieldOrder(fields)
    .filter((field) => !placed.has(fieldKey(field.id)))
    .map((field): TOrderItem => ({ key: fieldKey(field.id), kind: "field", fieldId: field.id }));
  return [...saved, ...rest];
}

/** The order as the server stores it. */
export function orderToSaved(order: readonly TOrderItem[]): TSavedItem[] {
  return order.map((item) =>
    item.kind === "field" ? { fieldId: item.fieldId } : { spacerFormId: item.formId }
  );
}

/** Whether an item belongs to one step: one of its fields, or an empty space on it. */
const inStep = (item: TOrderItem, stepFieldIds: ReadonlySet<number>, formId: number) =>
  item.kind === "field" ? stepFieldIds.has(item.fieldId) : item.formId === formId;

/** The items of one step, in the order they have in the whole list. */
export function stepItems(
  order: readonly TOrderItem[],
  stepFieldIds: ReadonlySet<number>,
  formId: number
): TOrderItem[] {
  return order.filter((item) => inStep(item, stepFieldIds, formId));
}

/**
 * Writes one step's new item order back into the whole list. The step's items
 * are taken out and put back together, in their new order, where the step's
 * first item was; every other item keeps its place. Only the order inside a
 * step matters to the form, so grouping a step's items together changes
 * nothing anywhere else.
 */
export function placeStepItems(
  order: readonly TOrderItem[],
  stepFieldIds: ReadonlySet<number>,
  formId: number,
  newStepItems: readonly TOrderItem[]
): TOrderItem[] {
  const firstIndex = order.findIndex((item) => inStep(item, stepFieldIds, formId));
  const others = order.filter((item) => !inStep(item, stepFieldIds, formId));
  if (firstIndex === -1) return [...others, ...newStepItems];
  const insertAt = order
    .slice(0, firstIndex)
    .filter((item) => !inStep(item, stepFieldIds, formId)).length;
  return [...others.slice(0, insertAt), ...newStepItems, ...others.slice(insertAt)];
}

/** A copy of `items` with the item at `from` moved to `to` (both 0-based, clamped to the list). */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const target = Math.max(0, Math.min(items.length - 1, to));
  if (from === target || from < 0 || from >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(target, 0, item);
  return next;
}

/* ------------------------------------------------------------------ */
/* Applicant form                                                      */
/* ------------------------------------------------------------------ */

/** One step of the form in its default layout, as RenderStep builds it. */
export type TStepLayout<F extends TLayoutField> = {
  startBefore: F[];
  block: {
    maujaField?: F;
    khasaraField?: F;
    plotNoField?: F;
    manualKhasaraField?: F;
  } | null;
  startAfter: F[];
  middleFields: F[];
  endFields: F[];
};

/** One thing the form draws in the Admin's order: a field, or an empty space. */
export type TSequencedItem<F extends TLayoutField> =
  | { kind: "field"; field: F }
  | { kind: "spacer"; key: string };

/**
 * Puts one step in the Admin's order. The step's default layout is flattened
 * (the Mauja/Khasara/Plot No. block becomes its separate fields), then sorted
 * by each field's place in `order`, with the empty spaces the Admin put on
 * this step (`formId`) at their places. A field the order does not mention
 * keeps its default place after the ones it does.
 *
 * The Manual Sub-Khasara entry RenderStep adds on its own (id -1, when a
 * service has Khasara No. without the full trio) takes the place of Khasara
 * No., so it stays straight after it.
 */
export function sequenceStepLayout<F extends TLayoutField>(
  layout: TStepLayout<F>,
  order: readonly TOrderItem[],
  formId: number
): TSequencedItem<F>[] {
  const blockFields = layout.block
    ? [
        layout.block.maujaField,
        layout.block.khasaraField,
        layout.block.plotNoField,
        layout.block.manualKhasaraField,
      ].filter((field): field is F => !!field)
    : [];

  const fields = [
    ...layout.startBefore,
    ...blockFields,
    ...layout.startAfter,
    ...layout.middleFields,
    ...layout.endFields,
  ].filter((field) => !neverDrawn.has(field.uid));

  const positionOf = new Map(order.map((item, index) => [item.key, index]));
  const khasaraId = fields.find((field) => field.uid === "khasara_no")?.id;

  const items: TSequencedItem<F>[] = [
    ...fields.map((field): TSequencedItem<F> => ({ kind: "field", field })),
    ...order
      .filter((item) => item.kind === "spacer" && item.formId === formId)
      .map((item): TSequencedItem<F> => ({ kind: "spacer", key: item.key })),
  ];

  const rankOf = (item: TSequencedItem<F>) => {
    if (item.kind === "spacer") return positionOf.get(item.key) ?? UNPLACED;
    const id =
      item.field.id < 0 && item.field.uid === "manual_khasara_no" ? khasaraId : item.field.id;
    if (typeof id !== "number") return UNPLACED;
    return positionOf.get(fieldKey(id)) ?? UNPLACED;
  };

  return stableSortByRank(items, rankOf);
}

/* ------------------------------------------------------------------ */
/* Review and view screens                                             */
/* ------------------------------------------------------------------ */

/**
 * Puts the one numbered list of a detail screen (L1/L2/L3 application
 * details, ViewApplication and its admin copy, the applicant's payment
 * details, the resubmit preview) in the Admin's order. The screen's steps are
 * not kept apart there, so the saved order is followed as one list across
 * every step:
 *
 * - the fields the order mentions come first, at their saved places;
 * - every field the order does not mention (the Plot Details fields, the
 *   Address added by the screen, fields added later) follows, in the order
 *   the screen gave them;
 * - the Admin's empty spaces are left out. They only shape the applicant
 *   form's layout; a detail screen lists its rows without gaps.
 *
 * `fields` must already be the rows the screen shows; a saved field the
 * screen hides stays hidden. With no order (switch off, nothing saved) the
 * list is returned as it came.
 *
 * With `stepIndexOf` (see makeStepIndexOf) the list follows the form's
 * steps instead: each step's fields in the saved order, then that step's
 * fields the order does not mention (an Inactive field that still holds a
 * value, a field switched back to Active after the Admin last saved), then
 * the fields that belong to no step. Without it, every field the order
 * does not mention follows all the ordered ones, as before.
 *
 * BEFORE - each empty space came back as a { kind: "spacer" } item that the
 * screens drew as a blank row (ReviewFieldSpacer), trimmed at the start and
 * end of the list:
 *   order.forEach((item) => {
 *     if (item.kind === "spacer") {
 *       placed.push({ kind: "spacer", key: item.key });
 *       return;
 *     }
 *     ...
 *   });
 *   const all = [...placed, ...rest];
 *   ... slice(first field, last field + 1)
 */
export function sequenceReviewFields<F extends TLayoutField>(
  fields: readonly F[],
  order: readonly TOrderItem[] | null | undefined,
  stepIndexOf?: (field: F) => number | undefined
): F[] {
  if (!order?.length) return [...fields];

  const byId = new Map(fields.map((field) => [field.id, field]));
  const placedIds = new Set<number>();
  const placed: F[] = [];
  order.forEach((item) => {
    if (item.kind === "spacer") return;
    const field = byId.get(item.fieldId);
    if (!field || placedIds.has(field.id)) return;
    placedIds.add(field.id);
    placed.push(field);
  });

  const rest = fields.filter((field) => !placedIds.has(field.id));
  if (!stepIndexOf) return [...placed, ...rest];

  /**
   * Rank = step first, then ordered fields before the step's leftovers.
   * Fields with no step share the last rank; the stable sort keeps the
   * ordered ones first there too.
   */
  const ranked = [
    ...placed.map((field) => ({ field, late: 0 })),
    ...rest.map((field) => ({ field, late: 1 })),
  ];
  return stableSortByRank(ranked, ({ field, late }) => {
    const step = stepIndexOf(field);
    return typeof step === "number" ? step * 2 + late : UNPLACED;
  }).map((entry) => entry.field);
}

/** One form step as the details screens load it (useApplicationFormSteps). */
type TStepForPlacement = {
  id: number | string;
  type?: string;
  fields?: readonly { uid: string }[];
};

/**
 * Which form step (0 = first) a field belongs to, for sequenceReviewFields.
 * A field on a step's own field list belongs to that step. Otherwise its
 * `formIds` (the forms it is mapped to, sent with /applications/all-fields)
 * are matched to the steps; this is how an Inactive field, which the steps
 * no longer list, still finds its step. Upload steps (Documents, NOC) are
 * not counted. Undefined when the field belongs to no step.
 */
export function makeStepIndexOf(steps: readonly TStepForPlacement[]) {
  const fieldSteps = steps.filter((step) => !["Documents", "NOC"].includes(step.type ?? ""));
  const stepByUid = new Map<string, number>();
  fieldSteps.forEach((step, index) =>
    (step.fields || []).forEach((field) => {
      if (!stepByUid.has(field.uid)) stepByUid.set(field.uid, index);
    })
  );
  const stepByFormId = new Map(fieldSteps.map((step, index) => [Number(step.id), index]));

  return (field: { uid: string; formIds?: readonly (number | string)[] }): number | undefined => {
    const own = stepByUid.get(field.uid);
    if (own !== undefined) return own;
    const mapped = (field.formIds || [])
      .map((formId) => stepByFormId.get(Number(formId)))
      .filter((index): index is number => index !== undefined);
    return mapped.length ? Math.min(...mapped) : undefined;
  };
}
